import { useRef } from 'react';

import { useInterval } from 'react-use';

import { sendMultiScreenEventToIpad } from 'services/helpers/multiScreen';
import { PlayerEvent } from 'shared-types/player-event';

const NUMBER_OF_SECONDS_AT_END_OF_SONG = 5;
const NUMBER_OF_SECONDS_IDLE_BEFORE_ENDING_SONG = 2;

const isPlayingEndOfSong = (currentTime: number, duration: number): boolean => {
  return currentTime > duration - NUMBER_OF_SECONDS_AT_END_OF_SONG;
};

const isIdle = (paused: boolean, currentTime: number, lastCurrentTime: number): boolean => {
  return !paused && currentTime === lastCurrentTime;
};

// [2021-09-01] WARNING
// This guard is a workaround to fix a bug encountered at end of songs with vocal guide.
// - bug description: in some cases (not always), the HTMLVideoElement "ended" event we rely
// on for moving to the next song would not be triggered when reaching the end of the song.
// - we observed that when the bug occurs, the video element is in the following state:
// the "paused" attribute is false, but the currentTime freezes just a few seconds before the end
// of the song
// - workaround: this guard checks every seconds if the activeVideoElement is in this state. If
// it is idle for more than 2 seconds, it triggers the SONG_END event to prevent staying indefinitely
// blocked without moving to the next song
// - ideal solution: understand why the activeVideoElement sometimes freezes - we did not have
// enough time to do it and find a better solution to this bug

export const useVocalGuideSongEndGuard = (activeVideoElement: HTMLVideoElement | null) => {
  const lastActiveVideoElementCurrentTime = useRef(0);
  const numberOfSecondsIdle = useRef(0);

  useInterval(() => {
    if (!activeVideoElement) {
      return;
    }

    if (
      isPlayingEndOfSong(activeVideoElement.currentTime, activeVideoElement.duration) &&
      isIdle(
        activeVideoElement.paused,
        activeVideoElement.currentTime,
        lastActiveVideoElementCurrentTime.current,
      )
    ) {
      numberOfSecondsIdle.current += 1;
    } else {
      numberOfSecondsIdle.current = 0;
    }

    if (numberOfSecondsIdle.current === NUMBER_OF_SECONDS_IDLE_BEFORE_ENDING_SONG) {
      lastActiveVideoElementCurrentTime.current = 0;
      numberOfSecondsIdle.current = 0;
      sendMultiScreenEventToIpad(PlayerEvent.SONG_END);
    }

    lastActiveVideoElementCurrentTime.current = activeVideoElement.currentTime;
  }, 1000);
};
